import React, { useEffect, useState } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    TextField,
    Button,
    LinearProgress,
    MenuItem,
    CircularProgress,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { getAllSportsBySchool } from "../../../../API/roster";
import { createNewTeamGear } from "../../../../API/teamGear";
import useNotification from "../../../../hooks/useNotification";
import { TeamGearPhotoUploader } from "./TeamGearPhotoUploader";
import { ListTeamGear } from "./ListTeamGear";

export default function CreateTeamGear({ school = "", user }) {
    const defaultTeamGear = {
        school_name: school,
        sport: "",
        email: "",
        phone: "",
        coach_name: "",
        paypal_email: "",
        venmo_phone: ""
    }
    const [newTeamGear, setNewTeamGear] = React.useState(defaultTeamGear);
    const [emailError, setEmailError] = useState("");
    const [paypalEmailError, setPaypalEmailError] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [venmoPhoneError, setVenmoPhoneError] = useState("");
    const [sportsList, setSportsList] = useState([]);
    const [sportsListLoading, setSportsListLoading] = React.useState(false);
    const [teamGearLoading, setTeamGearLoading] = React.useState(false);
    const [expanded, setExpanded] = React.useState(false);
    const [file, setFile] = useState(null);
    const [refresh, setRefresh] = useState(false);

    const notify = useNotification();

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhoto = (phone) => {
        const photoRegex = /^\d{10}$/;
        return photoRegex.test(phone);
    };

    useEffect(() => {
        const fetchSports = async () => {
            setSportsListLoading(true)
            try {
                let record = await getAllSportsBySchool(school, user)
                setSportsList(record.data)
            } catch (error) {
                console.error("error fetching sport record for school: " + school)
                notify("Unable to load sport list for team gear")
            } finally {
                setSportsListLoading(false)
            }
        }

        fetchSports()
    }, [user])

    const updateField = (field, value) => {
        setNewTeamGear((prev) => ({
            ...prev,
            [field]: value,
        }));

        // Validate email when it changes
        if (field === "email") {
            if (!validateEmail(value)) {
                setEmailError("Please enter a valid email address");
            } else {
                setEmailError("");
            }
        }

        if (field === "paypal_email") {
            if (value == "") {
                setPaypalEmailError("");
                return
            }

            if (!validateEmail(value)) {
                setPaypalEmailError("Please enter a valid email address");
            } else {
                setPaypalEmailError("");
            }
        }

        // Validate phone when it changes
        if (field === "phone") {
            if (value == "") {
                setPhoneError("");
                return
            }

            if (!validatePhoto(value)) {
                setPhoneError("Please enter a valid phone");
            } else {
                setPhoneError("");
            }
        }

        if (field === "venmo_phone") {
            if (value == "") {
                setVenmoPhoneError("");
                return
            }

            if (!validatePhoto(value)) {
                setVenmoPhoneError("Please enter a valid venmo phone");
            } else {
                setVenmoPhoneError("");
            }
        }
    };

    const handleExpandClick = () => {
        setExpanded((prev) => !prev);
    };

    const handleCreate = async () => {
        setTeamGearLoading(true);
        try {
            const response = await createNewTeamGear(file, { team: newTeamGear }, user);
            if (response.data.code === 200) {
                notify({ variant: "success", msg: "Team Gear created successfully." });
                setNewTeamGear(defaultTeamGear)
                setRefresh(true)
            } else {
                notify("Something went wrong.");
            }
        } catch (error) {
            if (error.response && error.response.status === 409) {
                notify(error.response.data.Err || "Team Gear already exists.");
            } else {
                console.error("Error creating new team gear:", error);
                notify("Failed to create new Team Gear.");
            }
        } finally {
            setTeamGearLoading(false);
        }
    };

    // Check if form is valid to enable/disable the Create button
    const isFormValid = () => {
        return (
            newTeamGear.sport &&
            !emailError &&
            !paypalEmailError &&
            newTeamGear.coach_name
            // && file
        );
    };

    return (
        <div>
            {teamGearLoading && <LinearProgress />}
            <Accordion expanded={expanded} disabled={sportsListLoading} onChange={handleExpandClick}>
                <AccordionSummary
                    expandIcon={!sportsListLoading ? <ArrowDownwardIcon /> : <CircularProgress />}
                    aria-controls="TeamGear-content"
                    id="TeamGear"
                >
                    <Typography>Team Gear</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box
                        sx={{
                            backgroundColor: "background.paper",
                            marginTop: "20px",
                            marginBottom: "20px",
                        }}
                    >
                        <div
                            style={{
                                paddingLeft: "20px",
                                paddingTop: "10px",
                                fontSize: "24px",
                                fontWeight: "bold",
                            }}
                        >
                            Create New Team Gear
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "10px",
                                width: "100%",
                                padding: "20px",
                                paddingTop: "10px",
                            }}
                        >
                            <TextField
                                fullWidth
                                style={{ width: "30vw" }}
                                label="School Name"
                                value={newTeamGear.school_name}
                                disabled
                            />
                            <TextField
                                fullWidth
                                select
                                style={{ width: "30vw" }}
                                label="Sport"
                                value={newTeamGear.sport}
                                onChange={(e) => updateField("sport", e.target.value)}
                            >
                                {sportsList.map((sport) => (
                                    <MenuItem key={sport} value={sport}>
                                        {sport}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                fullWidth
                                type="email"
                                style={{ width: "30vw" }}
                                label="Email"
                                value={newTeamGear.email}
                                onChange={(e) => updateField("email", e.target.value)}
                                error={!!emailError}
                                helperText={emailError}
                            />
                            <TextField
                                fullWidth
                                style={{ width: "30vw" }}
                                label="Phone"
                                value={newTeamGear.phone}
                                onChange={(e) => updateField("phone", e.target.value)}
                                error={!!phoneError}
                                helperText={phoneError}
                            />
                            <TextField
                                fullWidth
                                required
                                style={{ width: "30vw" }}
                                label="Coach Name"
                                value={newTeamGear.coach_name}
                                onChange={(e) => updateField("coach_name", e.target.value)}
                            />
                            <TextField
                                fullWidth
                                type="email"
                                style={{ width: "30vw" }}
                                label="Paypal Email"
                                value={newTeamGear.paypal_email}
                                onChange={(e) => updateField("paypal_email", e.target.value)}
                                error={!!paypalEmailError}
                                helperText={paypalEmailError}
                            />
                            <TextField
                                fullWidth
                                style={{ width: "30vw" }}
                                label="Venmo Phone"
                                value={newTeamGear.venmo_phone}
                                onChange={(e) => updateField("venmo_phone", e.target.value)}
                                error={!!venmoPhoneError}
                                helperText={venmoPhoneError}
                            />
                            <Box sx={{ margin: 1, width: "90%" }}>
                                <TeamGearPhotoUploader
                                    file={file}
                                    setFile={setFile}
                                />
                            </Box>
                            <div style={{ margin: "auto" }}>
                                <Button
                                    variant="contained"
                                    onClick={handleCreate}
                                    disabled={teamGearLoading || !isFormValid()}
                                >
                                    Create
                                </Button>
                            </div>
                        </div>
                    </Box>
                    <ListTeamGear
                        school={school}
                        user={user}
                        refresh={refresh}
                        setRefresh={setRefresh}
                    />
                </AccordionDetails>
            </Accordion>
        </div>
    );
}
